import React from 'react';

/**
 * ReferralStrategy Component
 * Displays the high-level referral strategy (Who/Why/Likelihood) before showing individual contacts
 * This helps users understand the best approach before diving into contact details
 */
const ReferralStrategy = ({ strategy, companyName, loading, error, onSaveForNetworking, allContactsData }) => {
    
    console.log('🎨 [ReferralStrategy] Render:', { 
        hasStrategy: !!strategy, 
        loading, 
        error,
        companyName,
        strategyKeys: strategy ? Object.keys(strategy) : [],
        allContactsData: allContactsData
    });
    // Helpers for initials avatar in Who list
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].slice(0,2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const getAvatarColor = (name) => {
        const colors = ['bg-blue-600','bg-indigo-600','bg-purple-600','bg-green-600','bg-emerald-600','bg-pink-600','bg-yellow-600'];
        if (!name) return colors[0];
        let sum = 0;
        for (let i=0;i<name.length;i++) sum += name.charCodeAt(i);
        return colors[sum % colors.length];
    };

    // Helper to get profile picture from various possible fields
    const getProfilePicture = (contact) => {
        if (!contact) return null;
        
        // First check the contact directly
        let picture = contact.profilePicture || 
                     contact.profile_picture || 
                     contact.profile_photo ||
                     contact.photo_url ||
                     contact.picture || 
                     contact.photo ||
                     contact.image ||
                     contact.profilePic ||
                     contact.avatar;
        
        // If not found and we have allContactsData, try to match by name and get their picture
        if (!picture && allContactsData && contact.name) {
            const matchingContact = allContactsData.find(c => 
                c.name && c.name.toLowerCase() === contact.name.toLowerCase()
            );
            
            if (matchingContact) {
                picture = matchingContact.profilePicture || 
                         matchingContact.profile_picture || 
                         matchingContact.profile_photo ||
                         matchingContact.photo_url ||
                         matchingContact.picture || 
                         matchingContact.photo ||
                         matchingContact.image ||
                         matchingContact.profilePic ||
                         matchingContact.avatar;
            }
        }
        
        // Check if picture is valid (not empty, not 'N/A', not placeholder)
        if (picture && 
            picture !== 'N/A' && 
            picture !== '' && 
            picture !== 'null' &&
            picture !== 'undefined' &&
            typeof picture === 'string' &&
            (picture.startsWith('http://') || picture.startsWith('https://'))) {
            return picture;
        }
        return null;
    };
    
    // Show loading state
    if (loading) {
        return (
            <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-base font-semibold text-slate-900">Generating referral strategy</h3>
                <p className="mt-2 text-sm text-slate-600">
                    Creating personalized networking plan based on your profile and available contacts...
                </p>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                    Unable to generate referral strategy. Showing contact analysis below.
                </p>
            </div>
        );
    }

    // Don't show if no strategy yet (still loading or not started)
    if (!strategy) {
        return null;
    }

    const getLikelihoodColor = (rating) => {
        const colors = {
            'Likely': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
            'Fair': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
            'Possible': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
            'Difficult': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' }
        };
        return colors[rating] || colors['Fair'];
    };

    const likelihoodStyle = getLikelihoodColor(strategy.likelihood?.rating);

    return (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            {/* Header */}
            <div className="mb-6 border-b border-slate-200 pb-4">
                <h3 className="text-xl font-semibold text-slate-900 text-left">{companyName} referral strategy</h3>
            </div>

            {/* WHO Section */}
            <div className="mb-6">
                <h4 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2 text-left">
                    <span className="bg-slate-900 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">1</span>
                    Who to Contact
                </h4>
                <p className="text-sm text-slate-600 mb-3 leading-relaxed text-left">{strategy.who?.summary}</p>
                {strategy.who?.primary_contacts && strategy.who.primary_contacts.length > 0 && (
                    <div className="space-y-2">
                        {strategy.who.primary_contacts.map((contact, idx) => {
                            const label = contact.name || `Contact ${idx + 1}`;
                            const title = contact.title || '';

                            const scrollToContact = (c) => {
                                try {
                                    console.log('🔎 [ReferralStrategy] Scroll request for contact:', c);
                                    // Candidate ids to try (most specific first)
                                    const candidateIds = [];
                                    if (c.contact_id !== undefined && c.contact_id !== null) candidateIds.push(`contact-${c.contact_id}`);
                                    if (c._uniqueId) candidateIds.push(`contact-${c._uniqueId}`);
                                    if (c.uniqueId) candidateIds.push(`contact-${c.uniqueId}`);

                                    for (const id of candidateIds) {
                                        const el = document.getElementById(id);
                                        if (el) {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            el.classList.add('ring-4', 'ring-blue-400', 'ring-opacity-50');
                                            setTimeout(() => el.classList.remove('ring-4', 'ring-blue-400', 'ring-opacity-50'), 2000);
                                            return;
                                        }
                                    }

                                    // Fallback by name match within contact cards
                                    if (c.name) {
                                        const nameLower = c.name.toLowerCase();
                                        const els = Array.from(document.querySelectorAll('[id^="contact-"]'));
                                        const match = els.find(e => e.innerText && e.innerText.toLowerCase().includes(nameLower));
                                        if (match) {
                                            match.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            match.classList.add('ring-4', 'ring-blue-400', 'ring-opacity-50');
                                            setTimeout(() => match.classList.remove('ring-4', 'ring-blue-400', 'ring-opacity-50'), 2000);
                                            return;
                                        }
                                    }

                                    console.warn('⚠️ [ReferralStrategy] No matching contact card found for', c.name || c.contact_id || idx);
                                } catch (err) {
                                    console.error('Error scrolling to contact', err);
                                }
                            };

                            const profilePic = getProfilePicture(contact);

                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => scrollToContact(contact)}
                                    className="w-full text-left flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                                >
                                    {/* Profile Picture or Initials avatar */}
                                    <div className="relative w-12 h-12 flex-shrink-0">
                                        {profilePic ? (
                                            <>
                                                <img 
                                                    src={profilePic} 
                                                    alt={contact.name}
                                                    className="w-12 h-12 rounded-full object-cover"
                                                    onError={(e) => {
                                                        // Hide image and show fallback initials on error
                                                        e.target.style.display = 'none';
                                                        if (e.target.nextElementSibling) {
                                                            e.target.nextElementSibling.style.display = 'flex';
                                                        }
                                                    }}
                                                />
                                                <div 
                                                    className={`${getAvatarColor(contact.name)} text-white rounded-full w-12 h-12 flex items-center justify-center font-bold absolute top-0 left-0`}
                                                    style={{ display: 'none' }}
                                                >
                                                    {getInitials(contact.name)}
                                                </div>
                                            </>
                                        ) : (
                                            <div className={`${getAvatarColor(contact.name)} text-white rounded-full w-12 h-12 flex items-center justify-center font-bold`}>
                                                {getInitials(contact.name)}
                                            </div>
                                        )}
                                    </div>
                                    <span className="bg-slate-100 text-slate-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                                        {idx + 1}
                                    </span>
                                    <div className="flex-1 text-left">
                                        <p className="font-semibold text-slate-900 text-left">{label}</p>
                                        <p className="text-sm text-slate-600 text-left">{title}</p>
                                        {contact.one_line_reason && (
                                            <p className="text-sm text-slate-500 mt-1 text-left">{contact.one_line_reason}</p>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* WHY Section */}
            <div className="mb-6">
                <h4 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2 text-left">
                    <span className="bg-slate-900 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">2</span>
                    Why This Strategy Works
                </h4>
                {strategy.why?.strategic_rationale && strategy.why.strategic_rationale.length > 0 && (
                    <ul className="space-y-2 mb-4">
                        {strategy.why.strategic_rationale.map((reason, idx) => (
                            <li key={idx} className="text-sm text-slate-700 pl-4 border-l-2 border-slate-300 py-1 text-left">
                                {reason}
                            </li>
                        ))}
                    </ul>
                )}
                
                {/* {strategy.why?.key_talking_points && strategy.why.key_talking_points.length > 0 && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm font-semibold text-green-900 mb-2">💡 Key Talking Points:</p>
                        <ul className="space-y-1">
                            {strategy.why.key_talking_points.map((point, idx) => (
                                <li key={idx} className="text-sm text-green-800 pl-4 border-l-2 border-green-400">
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </div>
                )} */}
            </div>

            {/* REFERRAL LIKELIHOOD Section */}
            <div className="mb-4">
                <h4 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2 text-left">
                    <span className="bg-slate-900 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">3</span>
                    Referral Likelihood
                </h4>

                {/* Likelihood Rating Badge */}
                <div className="mb-3 text-left">
                    <span className={`inline-block px-4 py-2 rounded-full text-base font-bold ${likelihoodStyle.bg} ${likelihoodStyle.text} border-2 ${likelihoodStyle.border}`}>
                        {strategy.likelihood?.rating || 'Fair'}
                    </span>
                </div>

                <p className="text-sm text-slate-600 mb-4 leading-relaxed text-left">
                    {strategy.likelihood?.reasoning}
                </p>

                {/* Success Factors */}
                {/* {strategy.likelihood?.success_factors && strategy.likelihood.success_factors.length > 0 && (
                    <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm font-semibold text-green-900 mb-2">✅ Success Factors:</p>
                        <ul className="space-y-1">
                            {strategy.likelihood.success_factors.map((factor, idx) => (
                                <li key={idx} className="text-sm text-green-800 pl-4 border-l-2 border-green-400">
                                    {factor}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {strategy.likelihood?.challenges && strategy.likelihood.challenges.length > 0 && (
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-sm font-semibold text-orange-900 mb-2">⚠️ Be Aware:</p>
                        <ul className="space-y-1">
                            {strategy.likelihood.challenges.map((challenge, idx) => (
                                <li key={idx} className="text-sm text-orange-800 pl-4 border-l-2 border-orange-400">
                                    {challenge}
                                </li>
                            ))}
                        </ul>
                    </div>
                )} */}
            </div>

            {/* Next Steps */}
            {/* {strategy.next_steps && strategy.next_steps.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <h5 className="text-base font-bold text-gray-800 mb-3">📋 Next Steps:</h5>
                    <ol className="space-y-2 list-decimal pl-5">
                        {strategy.next_steps.map((step, idx) => (
                            <li key={idx} className="text-sm text-gray-700">{step}</li>
                        ))}
                    </ol>
                </div>
            )} */}

            {/* Save for Networking Button */}
            {/* <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                    onClick={onSaveForNetworking}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                >
                    <Bookmark size={18} />
                    Save Job
                </button>
            </div> */}
        </div>
    );
};

export default ReferralStrategy;
